import React from 'react'
import GuideLayout from '@/components/help/GuideLayout'
import GuideSection from '@/components/help/GuideSection'
import GuideWidget from '@/components/help/GuideWidget'
import GuideTip from '@/components/help/GuideTip'
import GuideRelatedLinks from '@/components/help/GuideRelatedLinks'
import GapFillingAnimation from '@/components/help/widgets/GapFillingAnimation'
import type { SharedProps } from '@/types/shared'

export default function GapFilling({ currentUser, properties }: Pick<SharedProps, 'currentUser' | 'properties'>) {
  return (
    <GuideLayout
      currentUser={currentUser}
      properties={properties}
      title="Gap Filling"
      subtitle="Turn hard-to-sell single-night gaps into booked rooms."
    >
      <GuideSection title="The Orphan Night Problem">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Picture this: Monday and Tuesday are booked. Thursday and Friday are booked. Wednesday sits
          empty — a single-night gap between two bookings.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          These <strong className="text-foreground">"orphan nights"</strong> are the hardest inventory
          to sell. Most guests book 2+ nights. OTAs rank longer-stay properties higher. A single empty
          night between bookings often goes unsold.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Gap filling identifies these orphan nights and adjusts pricing to make them more attractive.
        </p>
      </GuideSection>

      <GuideSection title="How Gap Filling Works">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Gap filling runs at <strong className="text-foreground">stage 7 of 12</strong> in the rate
          pipeline — after demand adjustments have already been applied.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          The engine scans for dates where:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            The day before has a confirmed booking checking out, <strong className="text-foreground">AND</strong>
          </li>
          <li>
            The day after has a confirmed booking checking in, <strong className="text-foreground">AND</strong>
          </li>
          <li>
            The date itself has no booking
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          When a gap is detected, a negative modifier is applied to reduce the rate and encourage a
          booking. This only applies to{' '}
          <strong className="text-foreground">genuine single-night gaps</strong>, not multi-day empty
          stretches.
        </p>

        <GuideWidget
          title="Gap Detection"
          footnote="Watch how the engine identifies and prices orphan nights between bookings."
        >
          <GapFillingAnimation />
        </GuideWidget>
      </GuideSection>

      <GuideSection title="Why It Matters for Revenue">
        <p className="text-sm leading-relaxed text-muted-foreground">
          An empty room generates zero revenue. Even at a discounted rate, a filled orphan night is
          pure upside.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Beyond the room revenue, filled gaps{' '}
          <strong className="text-foreground">improve your occupancy metrics</strong> which feeds back
          into the demand model. Higher occupancy signals stronger demand, which can lift rates on
          surrounding dates.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Some properties also reduce cleaning costs — a guest bridging two bookings may mean one fewer
          turnover.
        </p>
      </GuideSection>

      <GuideSection title="Gap Filling and Minimum Stay">
        <p className="text-sm leading-relaxed text-muted-foreground">
          If you have a <strong className="text-foreground">minimum stay of 2 nights</strong>, orphan
          nights cannot be booked even at reduced rates. The rate drops, but the restriction blocks the
          booking.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Consider relaxing minimum stay restrictions for dates identified as orphan nights. Gap filling
          adjusts the rate — you may need to{' '}
          <strong className="text-foreground">adjust restrictions separately via overrides</strong>.
        </p>
      </GuideSection>

      <GuideSection title="Tips">
        <div className="space-y-3">
          <GuideTip type="do">
            Let gap filling work automatically. The engine detects gaps from your live booking data — no
            manual intervention needed.
          </GuideTip>
          <GuideTip type="do">
            Combine gap filling with relaxed minimum-stay restrictions for orphan dates to maximise fill
            rates.
          </GuideTip>
          <GuideTip type="dont">
            Do not override gap-filled dates back to full price. If the rate dropped to fill a gap,
            fighting it usually means the room stays empty.
          </GuideTip>
        </div>
      </GuideSection>

      <GuideRelatedLinks
        links={[
          { slug: 'demand-response', title: 'Demand Response' },
          { slug: 'overrides', title: 'Overrides' },
          { slug: 'occupancy-metrics', title: 'Occupancy & Metrics' },
        ]}
      />
    </GuideLayout>
  )
}
